/**
 * Stripe Checkout côté client.
 *
 * Point d'entrée unique pour démarrer un parcours de paiement :
 *   await startCheckout({ kind: "subscription", planId, cycle });
 *   await startCheckout({ kind: "topup", planId, quantity });
 *   await startCheckout({ kind: "pack", packId });
 *
 * Appelle la Cloud Function callable `createCheckoutSession` (voir
 * `functions/src/stripeCheckout.ts`) qui retourne une `{ url }` de session
 * Stripe, puis redirige via `window.location.assign`.
 *
 * Le `window.location.origin` est envoyé avec la requête pour que le serveur
 * construise les `success_url` / `cancel_url` — Stripe exige des URL absolues
 * et on veut rester sur le même environnement (prod/preview/dev).
 */

import { httpsCallable } from "firebase/functions";
import { functions } from "./firebase";
import type { PlanId, PackId, BillingCycle } from "../data/pricing";

export type CheckoutRequest =
  | { kind: "subscription"; planId: PlanId; cycle: BillingCycle }
  | { kind: "topup"; planId: PlanId; quantity: 1 | 5 | 10 | 25 }
  | { kind: "pack"; packId: PackId };

type CheckoutRequestWithOrigin = CheckoutRequest & { origin: string };

export type CheckoutResponse =
  | { ok: true; url: string }
  | { ok: false; code: "stripe-not-configured" | "invalid-request" | "already-subscribed" | "unknown"; message: string };

const callCreateSession = httpsCallable<
  CheckoutRequestWithOrigin,
  CheckoutResponse
>(functions, "createCheckoutSession");

export class CheckoutNotConfiguredError extends Error {
  constructor(message = "Paiement Stripe en cours de configuration.") {
    super(message);
    this.name = "CheckoutNotConfiguredError";
  }
}

/** L'utilisateur a déjà un abonnement actif. Surfacé séparément pour que le
 *  consumer (Pricing.tsx) puisse rediriger vers Settings → Abonnement plutôt
 *  que d'afficher une modale d'erreur générique. */
export class AlreadySubscribedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "AlreadySubscribedError";
  }
}

/** Démarre un parcours de checkout. Redirige sur succès, throw sinon. */
export async function startCheckout(req: CheckoutRequest): Promise<void> {
  let data: CheckoutResponse;
  try {
    const res = await callCreateSession({
      ...req,
      origin: window.location.origin,
    });
    data = res.data;
  } catch (e) {
    const err = e as { code?: string; message?: string };
    // La callable n'existe pas encore en prod ? Traiter comme scaffold.
    if (err.code === "functions/not-found") {
      throw new CheckoutNotConfiguredError();
    }
    throw new Error(err.message ?? "Impossible de démarrer le paiement.");
  }

  if (!data.ok) {
    if (data.code === "stripe-not-configured") {
      throw new CheckoutNotConfiguredError(data.message);
    }
    if (data.code === "already-subscribed") {
      throw new AlreadySubscribedError(data.message);
    }
    throw new Error(data.message);
  }

  window.location.assign(data.url);
}

// ─── Customer Portal ──────────────────────────────────────────────────────

export type PortalErrorCode =
  | "no-customer"
  | "no-subscription"
  | "switch-to-current-plan"
  | "invalid-request"
  | "unknown";

type PortalResponse =
  | { ok: true; url: string }
  | { ok: false; code: PortalErrorCode; message: string };

const callCreatePortal = httpsCallable<
  { origin: string; switchToPriceId?: string },
  PortalResponse
>(functions, "createBillingPortalSession");

/**
 * Ouvre le Stripe Customer Portal : moyens de paiement, factures, plan,
 * résiliation. Redirige comme `startCheckout`. Throw si l'utilisateur n'a
 * pas encore de `stripeCustomerId` (no-customer) — le client doit afficher
 * un message "souscrivez d'abord" plutôt qu'une modale d'erreur générique.
 *
 * Si `switchToPriceId` est fourni, le portail s'ouvre directement sur la
 * confirmation du changement de plan (avec preview du prorata Stripe).
 * Requiert que l'user ait une sub active — sinon throw avec
 * `code: "no-subscription"`.
 */
export async function openBillingPortal(
  opts: { switchToPriceId?: string } = {},
): Promise<void> {
  let data: PortalResponse;
  try {
    const res = await callCreatePortal({
      origin: window.location.origin,
      ...(opts.switchToPriceId
        ? { switchToPriceId: opts.switchToPriceId }
        : {}),
    });
    data = res.data;
  } catch (e) {
    const err = e as { code?: string; message?: string };
    throw new Error(err.message ?? "Impossible d'ouvrir le portail client.");
  }
  if (!data.ok) throw new PortalError(data.code, data.message);
  window.location.assign(data.url);
}

export class PortalError extends Error {
  constructor(
    public readonly code: PortalErrorCode,
    message: string,
  ) {
    super(message);
    this.name = "PortalError";
  }
}

// ─── Reactivate subscription ──────────────────────────────────────────────

type ReactivateResponse =
  | { ok: true; subscriptionId: string }
  | {
      ok: false;
      code: "no-subscription" | "not-cancelling" | "unknown";
      message: string;
    };

const callReactivate = httpsCallable<
  Record<string, never>,
  ReactivateResponse
>(functions, "reactivateSubscription");

/**
 * Annule une résiliation en cours (`cancel_at_period_end: true`). À utiliser
 * depuis Settings → Abonnement quand l'user a cliqué "Reprendre l'abonnement"
 * sur le banner "Résiliation en cours".
 *
 * Retourne `void` sur succès. Throw `ReactivateError` si l'appel échoue
 * (no-subscription, not-cancelling, unknown).
 *
 * Note : le state /billing/current.cancelAt est resync via le webhook
 * `customer.subscription.updated` qui suit l'API call — le hook `useEntitlements`
 * réfléchira la modif en quasi-temps réel.
 */
export async function reactivateSubscription(): Promise<void> {
  let data: ReactivateResponse;
  try {
    const res = await callReactivate({});
    data = res.data;
  } catch (e) {
    const err = e as { code?: string; message?: string };
    throw new ReactivateError(
      "unknown",
      err.message ?? "Impossible de réactiver l'abonnement.",
    );
  }
  if (!data.ok) throw new ReactivateError(data.code, data.message);
}

export class ReactivateError extends Error {
  constructor(
    public readonly code:
      | "no-subscription"
      | "not-cancelling"
      | "unknown",
    message: string,
  ) {
    super(message);
    this.name = "ReactivateError";
  }
}
